import { AppBar, Button, Dialog, DialogActions, DialogContent, Toolbar } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  GET_MARITIME_DIRECTORY_PORTS,
  GetMaritimeDirectoryPortResponse
} from '../../api/graphql/maritimeDirectoryPort/maritimeDirectoryPort.queries';
import { DataGrid, GridColDef, GridFooter, GridFooterContainer } from '@mui/x-data-grid';
import MaritimeDirectoryPortIsActive from './MaritimeDirectoryPortIsActive';
import MaritimeDirectoryPortDelete from './MaritimeDirectoryPortDelete';
import MaritimeDirectoryPortEditorContext from './MaritimeDirectoryPortEditorContext';
import './MaritimeDirectoryEditorStyle.css';
import AddIcon from '@mui/icons-material/Add';
import AddMaritimeDirectoryEditor from './AddMaritimeDirectoryEditor';
import { useApolloErrorHandler } from 'hooks';
import { counterPartyModel, maritimeDirectoryPortModel } from 'model';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import {
  TXT_ACTIVE,
  TXT_ADD,
  TXT_CLOSE,
  TXT_COUNTRY,
  TXT_INACTIVE,
  TXT_PORT,
  TXT_PORTS,
  TXT_REMOVE
} from '../../../../../shared/translations';
import { GridNoResultsOverlay } from 'components/GridNoResultsOverlay';

interface MaritimeDirectoryPortsEditorProps {
  counterParty?: Partial<counterPartyModel>;
  maritimeDirectoryId: string;
  onUpdated?: () => void;
  onClose: () => void;
}

const MaritimeDirectoryPortsEditor = (props: MaritimeDirectoryPortsEditorProps) => {
  const { t } = useTranslation();
  const { apolloErrorHandler } = useApolloErrorHandler();
  const [showAdd, setShowAdd] = useState(false);
  const hasChanged = useRef<boolean | null>(null);

  const toggleAdd = () => setShowAdd((prev) => !prev);

  const { loading, error, data, refetch } = useQuery<GetMaritimeDirectoryPortResponse>(GET_MARITIME_DIRECTORY_PORTS, {
    fetchPolicy: 'network-only',
    variables: {
      where: {
        maritimeDirectoryId: {
          eq: props.maritimeDirectoryId
        }
      },
      order: [
        {
          port: {
            name: 'ASC' as const
          }
        }
      ]
    }
  });

  const onIsActiveUpdated = () => {
    hasChanged.current = true;
  };

  const columns: GridColDef<Partial<maritimeDirectoryPortModel>>[] = [
    {
      field: 'name',
      headerName: capitalizeEveryWord(t(TXT_PORT)),
      flex: 2,
      cellClassName: 'cell-column',
      valueGetter: (params) => params.row.port?.name || ''
    },
    {
      field: 'country',
      headerName: capitalizeEveryWord(t(TXT_COUNTRY)),
      flex: 2,
      cellClassName: 'cell-column',
      valueGetter: (params) => params.row.port?.country || ''
    },
    {
      field: 'isActive',
      headerName: `${capitalizeEveryWord(t(TXT_ACTIVE))} / ${capitalizeEveryWord(t(TXT_INACTIVE))}`,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) =>
        props.counterParty ? (
          <MaritimeDirectoryPortIsActive
            counterPartyType="AGENT"
            counterParty={props.counterParty}
            maritimeDirectoryPort={params.row}
            onUpdated={onIsActiveUpdated}
          />
        ) : null
    },
    {
      field: 'delete',
      headerName: capitalizeEveryWord(t(TXT_REMOVE)),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => <MaritimeDirectoryPortDelete maritimeDirectoryPort={params.row} />
    }
  ];

  const customFooter = () => (
    <GridFooterContainer
      sx={(theme) => ({
        padding: `0 ${theme.spacing(1)}`
      })}
    >
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={toggleAdd}>
        {t(TXT_ADD)}
      </Button>
      <GridFooter
        sx={{
          border: 'none'
        }}
      />
    </GridFooterContainer>
  );

  const ports = useMemo(() => {
    return data?.maritimeDirectoryPorts || [];
  }, [data?.maritimeDirectoryPorts]);

  const refresh = () => {
    refetch();
    hasChanged.current = true;
  };

  const onClose = () => {
    if (!!hasChanged.current && props.onUpdated) {
      props.onUpdated();
    }
    props.onClose();
  };

  useEffect(() => {
    apolloErrorHandler(error);
  }, [apolloErrorHandler, error]);

  return (
    <MaritimeDirectoryPortEditorContext.Provider
      value={{
        maritimeDirectoryId: props.maritimeDirectoryId,
        refresh,
        hasChanged
      }}
    >
      {showAdd ? <AddMaritimeDirectoryEditor excludeIds={ports.map((p) => p.port!.id!)} onClose={toggleAdd} /> : null}
      <Dialog
        open={true}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: 'calc(100vh - 100px)'
          }
        }}
      >
        <AppBar position="relative">
          <Toolbar>{t(TXT_PORTS).toUpperCase()}</Toolbar>
        </AppBar>
        <DialogContent>
          <DataGrid
            rows={ports}
            columns={columns}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            loading={loading}
            slots={{
              noRowsOverlay: GridNoResultsOverlay,
              footer: customFooter
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={loading}>
            {t(TXT_CLOSE)}
          </Button>
        </DialogActions>
      </Dialog>
    </MaritimeDirectoryPortEditorContext.Provider>
  );
};

export default MaritimeDirectoryPortsEditor;
